'use client'

import { useCallback, useContext, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import api from '@/lib/api'
import { checkAllPermissions, checkAnyPermission, checkPermission } from '@/lib/permissions'
import { applyPortalAuthToken } from '@/lib/auth/azure-client'
import { OmiziAuthContextState, OmiziAuthContextValue } from '@/components/auth/omizi-auth-provider'
import { clearStoredAuthSession, normalizeAuthErrorMessage, readStoredAuthSession } from '@/lib/auth/session'

export type AuthState = OmiziAuthContextValue

function createUnsupportedActionResult(message: string) {
  return {
    success: false,
    error: message,
  }
}

/**
 * Main authentication hook for the Azure contract.
 * The hook consumes the shared provider-backed auth state so the portal
 * hydrates only once instead of per-component.
 */
export function useAuth(): AuthState {
  const auth = useContext(OmiziAuthContextState)

  if (!auth) {
    throw new Error('useAuth must be used within OmiziAuthProvider')
  }

  return auth
}

/**
 * Azure sign-in is handled by the Omizi auth control-plane.
 * These compatibility helpers preserve the existing portal API surface while
 * Clerk-specific flow code is removed.
 */
export function useSignIn() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const signIn = useCallback(async () => {
    setError('Azure sign-in is not performed inside the portal hook anymore. Use the Omizi Azure auth bootstrap flow.')
    return createUnsupportedActionResult('Azure sign-in is not performed inside the portal hook anymore. Use the Omizi Azure auth bootstrap flow.')
  }, [])

  const attemptMFAVerification = useCallback(async () => {
    setError('Azure MFA verification is handled by Microsoft Entra.')
    return createUnsupportedActionResult('Azure MFA verification is handled by Microsoft Entra.')
  }, [])

  const prepareSecondFactor = useCallback(async () => undefined, [])

  return {
    signIn,
    attemptMFAVerification,
    prepareSecondFactor,
    mfaRequired: false,
    supportedFactors: [] as Array<{ strategy: string; phoneNumber?: string }>,
    isLoading,
    error,
    clearError: () => setError(null),
  }
}

export function useSignUp() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const signUp = useCallback(async () => {
    setError('Azure external-user onboarding is handled by the Omizi auth control-plane, not this hook.')
    return createUnsupportedActionResult('Azure external-user onboarding is handled by the Omizi auth control-plane, not this hook.')
  }, [])

  const verifyEmail = useCallback(async () => {
    setError('Azure email verification is handled by Microsoft Entra External ID.')
    return createUnsupportedActionResult('Azure email verification is handled by Microsoft Entra External ID.')
  }, [])

  return {
    signUp,
    verifyEmail,
    isLoading,
    error,
    clearError: () => setError(null),
  }
}

export function useLogout() {
  const router = useRouter()
  const { refreshAuth } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const logout = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const currentSession = readStoredAuthSession()
      if (currentSession?.accessToken) {
        applyPortalAuthToken(currentSession.accessToken)
        try {
          await api.delete('/api/auth/session')
        } catch (deleteError) {
          setError(normalizeAuthErrorMessage(deleteError, 'Failed to clear the backend session during logout'))
        }
      }
    } finally {
      clearStoredAuthSession()
      applyPortalAuthToken(null)
      await refreshAuth()
      router.push('/')
      setIsLoading(false)
    }
  }, [refreshAuth, router])

  return {
    logout,
    isLoading,
    error,
  }
}

export function usePermissions() {
  const { isLoading: authLoading, permissions: authPermissions } = useAuth()

  const normalizedPermissions = useMemo(
    () => authPermissions.map((permission) => permission.trim()).filter(Boolean),
    [authPermissions]
  )
  const hasPermission = useCallback((permission: string) => checkPermission(normalizedPermissions, permission), [normalizedPermissions])
  const hasAnyPermission = useCallback((requiredPermissions: string[]) => checkAnyPermission(normalizedPermissions, requiredPermissions), [normalizedPermissions])
  const hasAllPermissions = useCallback((requiredPermissions: string[]) => checkAllPermissions(normalizedPermissions, requiredPermissions), [normalizedPermissions])

  return {
    permissions: normalizedPermissions,
    isLoading: authLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refetch: async () => normalizedPermissions,
    error: null,
  }
}
